import { cloneDeep, get } from "lodash-es";
import type { Op } from "sharedb/lib/client";

import type {
  AccessIdDataType,
  AccessIdMetaDataType,
  PathType,
  RichTextCellType,
  TableCellType,
  WorksheetType,
} from "../types/worksheet";

import { ACCESS_ID_DATA_META_FIELDS, ACCESS_ID_WRAPPER, DATA_PATH, VALUE_KEY } from "./constants";
import { jsonOTType } from "./ot-types/ot-patch";

export const WORKSHEET_FIELD_CONTENT_MIN_CHARS = 10;

/** Table field cell map key on answer data (`d.<accessId>.<fieldId>.cm`). */
const TABLE_CELL_MAP_KEY = "cm";

/** Rich-text delta list key on a field or cell value. */
const RICH_TEXT_OPS_ARRAY_KEY = "ops";

/** Answer field path: `d.<accessId>.<fieldId>`. */
const ANSWER_FIELD_PATH_LENGTH = 3;

/** Table cell rich-text path: `d.<accessId>.<fieldId>.cm.<cellId>.v`. */
const TABLE_CELL_RICH_TEXT_PATH_LENGTH = 6;

/** Op path through cell map without value: `d.<accessId>.<fieldId>.cm.<cellId>`. */
const TABLE_CELL_MAP_OP_PATH_LENGTH = 5;

const PATH_DATA_ROOT_INDEX = 0;
const PATH_ACCESS_ID_INDEX = 1;
const PATH_FIELD_ID_INDEX = 2;
const PATH_TABLE_CELL_MAP_INDEX = 3;
const PATH_CELL_ID_INDEX = 4;
const PATH_CELL_VALUE_INDEX = 5;

/** Index of `ops` in `…ops.<n>` list-delete paths; `n` is always the last segment. */
const RICH_TEXT_OPS_LIST_DELETE_INDEX_OFFSET = 2;

export function fieldPathKey(fieldPath: PathType): string {
  return fieldPath.join(".");
}

function pathSegment(path: PathType, index: number): string | undefined {
  const segment = path[index];
  return typeof segment === "string" ? segment : undefined;
}

function isAnswerDataPath(path: PathType): boolean {
  return path[PATH_DATA_ROOT_INDEX] === DATA_PATH;
}

function isAnswerFieldPath(path: PathType): boolean {
  return path.length === ANSWER_FIELD_PATH_LENGTH && isAnswerDataPath(path);
}

function isTableCellRichTextPath(path: PathType): boolean {
  return (
    path.length === TABLE_CELL_RICH_TEXT_PATH_LENGTH &&
    path[PATH_TABLE_CELL_MAP_INDEX] === TABLE_CELL_MAP_KEY &&
    path[PATH_CELL_VALUE_INDEX] === VALUE_KEY
  );
}

function isAccessIdMetaFieldSegment(fieldSegment: string | undefined): boolean {
  return (
    fieldSegment !== undefined &&
    ACCESS_ID_DATA_META_FIELDS.includes(fieldSegment as keyof AccessIdMetaDataType)
  );
}

function getRichTextOpsList(value: unknown): RichTextCellType["ops"] | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  if ("ops" in value && Array.isArray((value as RichTextCellType).ops)) {
    return (value as RichTextCellType).ops;
  }
  const tableCellValue = value as TableCellType;
  const nestedRichText = tableCellValue.v;
  if (
    nestedRichText &&
    typeof nestedRichText === "object" &&
    "ops" in nestedRichText &&
    Array.isArray(nestedRichText.ops)
  ) {
    return nestedRichText.ops;
  }
  return null;
}

function plainTextFromRichTextValue(value: unknown): string {
  const ops = getRichTextOpsList(value);
  if (!ops) {
    return "";
  }
  return ops.map((op) => (typeof op.insert === "string" ? op.insert : "")).join("");
}

function plainTextFromTableField(fieldValue: unknown): string {
  if (!fieldValue || typeof fieldValue !== "object" || !(TABLE_CELL_MAP_KEY in fieldValue)) {
    return plainTextFromRichTextValue(fieldValue);
  }
  const cellMap = (fieldValue as { cm?: Record<string, TableCellType> }).cm;
  if (!cellMap || typeof cellMap !== "object") {
    return plainTextFromRichTextValue(fieldValue);
  }
  return Object.values(cellMap)
    .map((cell) => plainTextFromRichTextValue(cell?.v))
    .join("");
}

/** Plain text at a content path: answer field, whole table field, or one table cell. */
export function plainTextAtPath(doc: WorksheetType, contentPath: PathType): string {
  if (isTableCellRichTextPath(contentPath)) {
    return plainTextFromRichTextValue(get(doc, contentPath));
  }

  if (isAnswerFieldPath(contentPath)) {
    return plainTextFromTableField(get(doc, contentPath));
  }

  return plainTextFromRichTextValue(get(doc, contentPath));
}

export function fieldPathFromOpPath(opPath: PathType): PathType | null {
  if (!isAnswerDataPath(opPath) || !pathSegment(opPath, PATH_ACCESS_ID_INDEX)) {
    return null;
  }
  if (opPath.length < ANSWER_FIELD_PATH_LENGTH || !pathSegment(opPath, PATH_FIELD_ID_INDEX)) {
    return null;
  }
  if (isAccessIdMetaFieldSegment(pathSegment(opPath, PATH_FIELD_ID_INDEX))) {
    return null;
  }
  return opPath.slice(0, ANSWER_FIELD_PATH_LENGTH);
}

/** Narrowest answer content path for an op: field-level rich text or one table cell. */
export function fieldContentPathFromOpPath(opPath: PathType): PathType | null {
  const fieldPath = fieldPathFromOpPath(opPath);
  if (!fieldPath) {
    return null;
  }

  const cellMapSegment = opPath[PATH_TABLE_CELL_MAP_INDEX];
  const cellId = pathSegment(opPath, PATH_CELL_ID_INDEX);
  if (cellMapSegment !== TABLE_CELL_MAP_KEY || !cellId) {
    return fieldPath;
  }

  if (opPath[PATH_CELL_VALUE_INDEX] === VALUE_KEY) {
    return opPath.slice(0, TABLE_CELL_RICH_TEXT_PATH_LENGTH);
  }

  if (opPath.length === TABLE_CELL_MAP_OP_PATH_LENGTH) {
    return [...fieldPath, TABLE_CELL_MAP_KEY, cellId, VALUE_KEY];
  }

  return fieldPath;
}

export function opAffectsField(op: Op, contentPath: PathType): boolean {
  if (!op.p || op.p.length < contentPath.length) {
    return false;
  }
  return contentPath.every((segment, index) => op.p[index] === segment);
}

export function relevantOpsForField(ops: Op[], contentPath: PathType): Op[] {
  return ops.filter((op) => opAffectsField(op, contentPath));
}

export function opsTouchAnswerData(ops: Op[]): boolean {
  return ops.some((op) => op.p?.[PATH_DATA_ROOT_INDEX] === DATA_PATH);
}

export function getAffectedAnswerFieldPaths(ops: Op[]): PathType[] {
  const pathsByKey = new Map<string, PathType>();
  for (const op of ops) {
    if (op.p) {
      const contentPath = fieldContentPathFromOpPath(op.p as PathType);
      if (contentPath) {
        pathsByKey.set(fieldPathKey(contentPath), contentPath);
      }
    }
  }
  return [...pathsByKey.values()];
}

export function plainTextsForFieldPaths(
  doc: WorksheetType,
  fieldPaths: PathType[],
): Record<string, string> {
  return Object.fromEntries(
    fieldPaths.map((fieldPath) => [fieldPathKey(fieldPath), plainTextAtPath(doc, fieldPath)]),
  );
}

export function isSignificantFieldTextLoss(
  charsLost: number,
  charsBefore: number,
  charsAfter: number,
): boolean {
  const fieldEmptied = charsBefore > 0 && charsAfter === 0;
  return charsLost >= WORKSHEET_FIELD_CONTENT_MIN_CHARS || fieldEmptied;
}

export function isRichTextOpsListDelete(op: Op): boolean {
  if (!("ld" in op) || op.ld === undefined || !op.p) {
    return false;
  }
  const opsArrayIndex = op.p.indexOf(RICH_TEXT_OPS_ARRAY_KEY);
  const invalidOpIndex = op.p.length - RICH_TEXT_OPS_LIST_DELETE_INDEX_OFFSET;
  return opsArrayIndex >= 0 && opsArrayIndex === invalidOpIndex;
}

/** Minimal worksheet containing only one answer field or table cell, for scoped OT apply. */
export function buildScopedFieldDoc(snapshot: WorksheetType, contentPath: PathType): WorksheetType {
  const { accessId, fieldId, cellId } = fieldContentLogIds(contentPath);
  if (!accessId || !fieldId) {
    return { b: [], d: {}, locked: false };
  }

  if (cellId) {
    const accessIdData = {
      ...ACCESS_ID_WRAPPER,
      [fieldId]: {
        [TABLE_CELL_MAP_KEY]: {
          [cellId]: { v: cloneDeep(get(snapshot, contentPath) ?? {}) },
        },
      },
    } as AccessIdDataType;
    return {
      b: [],
      d: {
        [accessId]: accessIdData,
      },
      locked: false,
    };
  }

  const accessIdData = {
    ...ACCESS_ID_WRAPPER,
    [fieldId]: cloneDeep(get(snapshot, contentPath) ?? {}),
  } as AccessIdDataType;
  return {
    b: [],
    d: {
      [accessId]: accessIdData,
    },
    locked: false,
  };
}

export function plainTextAfterScopedApply(
  snapshot: WorksheetType,
  contentPath: PathType,
  relevantOps: Op[],
): string {
  if (relevantOps.length === 0) {
    return plainTextAtPath(snapshot, contentPath);
  }

  const afterDoc = jsonOTType.type.apply(
    buildScopedFieldDoc(snapshot, contentPath),
    relevantOps,
  ) as WorksheetType;
  return plainTextAtPath(afterDoc, contentPath);
}

export function fieldContentLogIds(contentPath: PathType): {
  accessId: string | undefined;
  fieldId: string | undefined;
  cellId: string | undefined;
} {
  const hasTableCell =
    contentPath[PATH_TABLE_CELL_MAP_INDEX] === TABLE_CELL_MAP_KEY &&
    typeof contentPath[PATH_CELL_ID_INDEX] === "string";

  return {
    accessId: pathSegment(contentPath, PATH_ACCESS_ID_INDEX),
    fieldId: pathSegment(contentPath, PATH_FIELD_ID_INDEX),
    cellId: hasTableCell ? pathSegment(contentPath, PATH_CELL_ID_INDEX) : undefined,
  };
}
